import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage } from '../api.js'
import { newShareToken } from '../ids.js'
import { combine, optimistic } from '../optimistic.js'
import { useToast } from '../../components/ui/Toast.jsx'
import { filtersOfKey, patchInPages, removeFromPages, upsertInPages } from './conversation-cache.js'
import { qk } from './keys.js'

function listPath(filters, cursor) {
  const params = new URLSearchParams({ limit: '40' })
  if (filters.q) params.set('q', filters.q)
  if (filters.system) params.set('system', filters.system)
  if (filters.combined) params.set('combined', '1')
  if (cursor) params.set('cursor', cursor)
  return `/conversations?${params.toString()}`
}

export function useConversationList(filters) {
  return useInfiniteQuery({
    queryKey: qk.conversationList(filters),
    queryFn: ({ pageParam, signal }) => api(listPath(filters, pageParam), { signal }),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15_000,
  })
}

export function useConversation(id, { enabled = true } = {}) {
  return useQuery({
    queryKey: qk.conversation(id),
    queryFn: ({ signal }) => api(`/conversations/${id}`, { signal }),
    enabled: Boolean(id) && enabled,
    retry: false,
  })
}

/**
 * Put a conversation into every cached list it matches (and out of the ones
 * it no longer matches). Used for the optimistic "new question" row and for
 * bumping a conversation to the top when a follow-up is sent.
 */
export function upsertConversationInLists(queryClient, conversation) {
  for (const [key, data] of queryClient.getQueriesData({ queryKey: qk.conversationLists })) {
    if (data) queryClient.setQueryData(key, upsertInPages(data, conversation, filtersOfKey(key)))
  }
}

export function invalidateConversation(queryClient, id) {
  queryClient.invalidateQueries({ queryKey: qk.conversationLists })
  if (id) queryClient.invalidateQueries({ queryKey: qk.conversation(id) })
}

// ---- Rename -----------------------------------------------------------------

export function useRenameConversation() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ id, title }) => api(`/conversations/${id}`, { method: 'PATCH', body: { title } }),
    onMutate: async ({ id, title }) => {
      const rollbackLists = await optimistic(queryClient, { queryKey: qk.conversationLists }, (data, key) =>
        patchInPages(data, id, { title, titleSource: 'user' }, filtersOfKey(key)),
      )
      const rollbackDetail = await optimistic(queryClient, { queryKey: qk.conversation(id) }, (data) => ({
        ...data,
        conversation: { ...data.conversation, title, titleSource: 'user' },
      }))
      return { rollback: combine(rollbackLists, rollbackDetail) }
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Couldn't rename — ${errorMessage(error)}`)
    },
    onSettled: (_data, _error, { id }) => invalidateConversation(queryClient, id),
  })
}

// ---- Delete with undo -------------------------------------------------------

export function useDeleteConversation() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const restore = useMutation({
    mutationFn: ({ id }) => api(`/conversations/${id}`, { method: 'PATCH', body: { restore: true } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.conversationLists }),
  })
  return useMutation({
    mutationFn: ({ id }) => api(`/conversations/${id}`, { method: 'DELETE' }),
    onMutate: async ({ id }) => {
      const rollback = await optimistic(queryClient, { queryKey: qk.conversationLists }, (data) =>
        removeFromPages(data, id),
      )
      return { rollback }
    },
    onSuccess: (_data, { id, title }) => {
      queryClient.removeQueries({ queryKey: qk.conversation(id) })
      toast.success(`Deleted “${title ?? 'conversation'}”`, {
        action: { label: 'Undo', onClick: () => restore.mutate({ id }) },
      })
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Couldn't delete — ${errorMessage(error)}`)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.conversationLists }),
  })
}

// ---- Feedback ---------------------------------------------------------------

export function useSetFeedback(conversationId) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ messageId, rating }) =>
      api(`/conversations/${conversationId}/feedback/${encodeURIComponent(messageId)}`, {
        method: 'PUT',
        body: { rating },
      }),
    onMutate: async ({ messageId, rating }) => {
      const rollback = await optimistic(queryClient, { queryKey: qk.conversation(conversationId) }, (data) => {
        const feedback = { ...(data.feedback ?? {}) }
        if (rating === 0) delete feedback[messageId]
        else feedback[messageId] = rating
        return { ...data, feedback }
      })
      return { rollback }
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Feedback not saved — ${errorMessage(error)}`)
    },
  })
}

// ---- Share ------------------------------------------------------------------

/**
 * The token is generated here, so the link exists (and can be copied) before
 * the server confirms it. If the conversation already had a link, the server
 * returns that one and the cache is corrected.
 */
export function useShareConversation(conversationId) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ token }) => api(`/conversations/${conversationId}/share`, { method: 'PUT', body: { token } }),
    onMutate: async ({ token }) => {
      const rollback = await optimistic(queryClient, { queryKey: qk.conversation(conversationId) }, (data) => ({
        ...data,
        share: data.share ?? { id: token, createdAt: Date.now() },
      }))
      return { rollback }
    },
    onSuccess: ({ share }) => {
      queryClient.setQueryData(qk.conversation(conversationId), (data) => (data ? { ...data, share } : data))
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`The share link didn't save — ${errorMessage(error)}`)
    },
  })
}

export { newShareToken }

export function useRevokeShare(conversationId) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: () => api(`/conversations/${conversationId}/share`, { method: 'DELETE' }),
    onMutate: async () => {
      const rollback = await optimistic(queryClient, { queryKey: qk.conversation(conversationId) }, (data) => ({
        ...data,
        share: null,
      }))
      return { rollback }
    },
    onSuccess: () => toast.success('Link turned off'),
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Couldn't turn off the link — ${errorMessage(error)}`)
    },
  })
}

export function useSharedConversation(token) {
  return useQuery({
    queryKey: qk.share(token),
    queryFn: ({ signal }) => api(`/shares/${token}`, { signal }),
    retry: false,
  })
}
